// V4-ANALYTICS-POSTS-HISTORY 2026-06-04
/**
 * Publications history panel (right sidebar, post-publication).
 *
 * Port fidèle de la maquette Figma de Sébastien (`HistorySection.tsx`,
 * "Dashboard for Social Monitoring") : timeline SVG custom (1 point = 1 post publié,
 * cliquable → ouvre le post), courbes Bézier par canal, auto-scroll sur "aujourd'hui",
 * + panneau latéral animé (hover sur un canal) à 5 accordéons (Sujets / Sources /
 * Activité / Stats globales / Positionnement). Toutes les agrégations du panneau sont
 * calculées côté client à partir de la liste de posts renvoyée par l'endpoint
 * `GET /api/v1/spaces/{id}/analytics/posts-history` (cf. zernioAnalyticsService).
 *
 * Dégradation gracieuse : pas de snapshots collectés → métriques à 0 (le scheduler de
 * snapshotting tourne en prod uniquement ; cf. backlog V4-ANALYTICS-TIMESERIES-BACKFILL).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart2, MessageSquare, ThumbsUp, Share2, ExternalLink, Calendar,
  TrendingUp, Sparkles, ChevronDown, Loader2,
} from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getLocaleFromLanguage } from '../../../i18n';
import { useAuthContext } from '../../../contexts/AuthContext';
import {
  zernioAnalyticsService,
  type PostHistoryItem,
  type ChannelHistoryMeta,
  type PostHistoryAnalytics,
} from '../../../services/zernioAnalyticsService';
import { getChannelDisplayConfig, ChannelSvgIcon } from '../../../utils/Constants';
import { PillToggleGroup } from '../../common/PillToggleGroup';
import { channelsService } from '../../../services/channelsService';
import { Logger } from '../../../utils/logger';

interface HistoryTabProps {
  /** Clic sur un point de la timeline → ouvre le post correspondant (résolu par id). */
  onPostClick?: (postId: string) => void;
}

type TimeFrame = 'daily' | 'weekly' | 'monthly';
type Metric = 'views' | 'likes' | 'comments' | 'shares';

const METRIC_FIELD: Record<Metric, keyof PostHistoryAnalytics> = {
  views: 'views', likes: 'likes', comments: 'comments_count', shares: 'shares',
};
const mv = (a: PostHistoryAnalytics, m: Metric): number => Number(a?.[METRIC_FIELD[m]] ?? 0);

function Accordion({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden flex flex-col mb-3 shrink-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-transparent hover:bg-[var(--color-background)] transition-colors text-sm font-medium text-[var(--color-text-primary)]"
      >
        {title}
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-4 h-4 text-[var(--color-text-secondary)] opacity-70" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function HistoryTab({ onPostClick }: HistoryTabProps) {
  const { t, language } = useLanguage();
  const locale = getLocaleFromLanguage(language);
  const { currentSpace } = useAuthContext();
  const h = (t.history ?? {}) as Record<string, string>;
  const lbl = (key: string, fallback: string) => h[key] ?? fallback;

  const [timeFrame, setTimeFrame] = useState<TimeFrame>('daily');
  const [metric, setMetric] = useState<Metric>('views');
  const [hoveredPoint, setHoveredPoint] = useState<{ x: number; y: number; post: PostHistoryItem } | null>(null);
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  const [renderedChannel, setRenderedChannel] = useState<ChannelHistoryMeta | null>(null);

  const [posts, setPosts] = useState<PostHistoryItem[]>([]);
  const [channels, setChannels] = useState<ChannelHistoryMeta[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // V4-HISTORY-POSITIONING-ACTION — positionnement éditable/généré par canal (réutilise
  // channelsService.generate/improve/update, même backend que la page Canaux).
  const [posDraft, setPosDraft] = useState<Record<string, string>>({});
  const [posBusy, setPosBusy] = useState<'idle' | 'generating' | 'saving'>('idle');

  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [baseWidth, setBaseWidth] = useState(1000);

  // Fetch posts-history for the current space.
  useEffect(() => {
    if (!currentSpace?.id) return;
    let cancelled = false;
    setIsLoading(true);
    zernioAnalyticsService
      .getSpacePostsHistory(currentSpace.id, { days: 365 })
      .then((res) => {
        if (cancelled) return;
        setPosts(res.posts ?? []);
        setChannels(res.channels ?? []);
      })
      .catch((err) => {
        Logger.error('[HistoryTab] Failed to load posts history', err);
        if (!cancelled) { setPosts([]); setChannels([]); }
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [currentSpace?.id]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => setBaseWidth(entries[0].contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (activeChannelId) {
      const channel = channels.find((c) => c.id === activeChannelId);
      if (channel) {
        setRenderedChannel(channel);
        // Initialise le brouillon de positionnement depuis la valeur du canal (une fois).
        setPosDraft((prev) => (prev[channel.id] !== undefined ? prev : { ...prev, [channel.id]: channel.positioning || '' }));
      }
    }
  }, [activeChannelId, channels]);

  // Génère (ou améliore) le positionnement spécifique du canal via IA, puis remplit le brouillon.
  const handleGeneratePositioning = async (channel: ChannelHistoryMeta) => {
    if (!currentSpace?.id || posBusy !== 'idle') return;
    setPosBusy('generating');
    try {
      const res = (channel.positioning || posDraft[channel.id])
        ? await channelsService.improvePositioning(channel.id, currentSpace.id)
        : await channelsService.generatePositioning(currentSpace.id, 'external', channel.network);
      setPosDraft((prev) => ({ ...prev, [channel.id]: res.positioning || '' }));
    } catch (err) {
      Logger.error('[HistoryTab] positioning generation failed', err);
    } finally {
      setPosBusy('idle');
    }
  };

  // Persiste le positionnement édité (PUT /channels/{id}) + met à jour le state local.
  const handleSavePositioning = async (channel: ChannelHistoryMeta) => {
    if (posBusy !== 'idle') return;
    const value = posDraft[channel.id] ?? '';
    setPosBusy('saving');
    try {
      await channelsService.update(channel.id, { positioning: value });
      setChannels((prev) => prev.map((c) => (c.id === channel.id ? { ...c, positioning: value } : c)));
      setRenderedChannel((prev) => (prev && prev.id === channel.id ? { ...prev, positioning: value } : prev));
    } catch (err) {
      Logger.error('[HistoryTab] positioning save failed', err);
    } finally {
      setPosBusy('idle');
    }
  };

  // V4-HISTORYTAB-CONNECTED-ONLY — n'afficher que les canaux réellement connectés
  // à Zernio (champ `connected` côté backend). Les coquilles vides (LinkedIn/FB/X
  // non connectés) ne servent à rien dans l'Historique et sont masquées.
  const enabledChannels = useMemo(
    () => channels.filter((c) => c.enabled !== false && c.connected === true),
    [channels],
  );

  // Posts par canal, triés chronologiquement (par published_date).
  const postsByChannel = useMemo(() => {
    const map: Record<string, PostHistoryItem[]> = {};
    for (const p of posts) {
      const t0 = p.published_date ? new Date(p.published_date).getTime() : 0;
      if (!Number.isFinite(t0) || t0 === 0) continue;
      (map[p.channel_id] ||= []).push(p);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => new Date(a.published_date).getTime() - new Date(b.published_date).getTime());
    }
    return map;
  }, [posts]);

  // Périodes (colonnes de fond) selon la granularité.
  const periodsData = useMemo(() => {
    const now = new Date();
    let maxPeriods = 365;
    let visibleCount = 7;
    let minPeriods = 7;
    if (timeFrame === 'weekly') { maxPeriods = 52; visibleCount = 4; minPeriods = 4; }
    else if (timeFrame === 'monthly') { maxPeriods = 12; visibleCount = 2; minPeriods = 3; }

    // V4-HISTORYTAB-DATE-AXIS-FIT 2026-06-11 — caler l'axe sur l'intervalle réel
    // des posts (du plus ancien à aujourd'hui) au lieu d'une fenêtre fixe d'un an,
    // qui affichait des colonnes vides remontant à l'année précédente (« 12 juin
    // 2025 » alors qu'on est en 2026). On garde un minimum lisible et on plafonne
    // au max par granularité. L'année courante vient de `new Date()` (today).
    const DAY_MS = 86_400_000;
    const postTimes = posts
      .map((p) => (p.published_date ? new Date(p.published_date).getTime() : NaN))
      .filter((t) => !Number.isNaN(t));
    const earliest = postTimes.length ? Math.min(...postTimes) : now.getTime();
    let span: number;
    if (timeFrame === 'daily') {
      span = Math.floor((now.getTime() - earliest) / DAY_MS) + 1;
    } else if (timeFrame === 'weekly') {
      span = Math.floor((now.getTime() - earliest) / (7 * DAY_MS)) + 1;
    } else {
      const e = new Date(earliest);
      span = (now.getFullYear() - e.getFullYear()) * 12 + (now.getMonth() - e.getMonth()) + 1;
    }
    const numPeriods = Math.min(maxPeriods, Math.max(minPeriods, span));

    const periods = Array.from({ length: numPeriods }, (_, i) => {
      const start = new Date(now);
      const end = new Date(now);
      const offset = numPeriods - 1 - i;
      if (timeFrame === 'daily') {
        start.setDate(now.getDate() - offset); start.setHours(0, 0, 0, 0);
        end.setDate(start.getDate() + 1); end.setHours(0, 0, 0, 0);
      } else if (timeFrame === 'weekly') {
        start.setDate(now.getDate() - offset * 7); start.setHours(0, 0, 0, 0);
        end.setDate(start.getDate() + 7); end.setHours(0, 0, 0, 0);
      } else {
        start.setMonth(now.getMonth() - offset); start.setDate(1); start.setHours(0, 0, 0, 0);
        end.setMonth(start.getMonth() + 1); end.setDate(1); end.setHours(0, 0, 0, 0);
      }
      let label = '';
      // V4-HISTORYTAB-DATE-AXIS-FUTURE 2026-06-11 — l'axe couvre 365j/52sem en
      // arrière. Sans l'année, une colonne d'il y a un an ("12 juin" 2025) est
      // indiscernable d'une date proche ("12 juin" 2026) et se lit comme une
      // date future. On ajoute l'année dès que la période n'est pas dans l'année
      // courante.
      const dayOpts: Intl.DateTimeFormatOptions =
        start.getFullYear() === now.getFullYear()
          ? { day: 'numeric', month: 'short' }
          : { day: 'numeric', month: 'short', year: 'numeric' };
      if (timeFrame === 'daily') label = start.toLocaleDateString(locale, dayOpts);
      else if (timeFrame === 'weekly') label = `${lbl('weekAbbrev', 'Sem')} ${start.toLocaleDateString(locale, dayOpts)}`;
      else label = start.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
      return { start, end, label };
    });

    const periodWidth = (baseWidth - 80) / visibleCount;
    return { periods, visibleCount, periodWidth };
  }, [timeFrame, baseWidth, locale, posts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stats agrégées par canal (top tags/sources, activité, stats globales, variations).
  const channelStats = useMemo(() => {
    const stats: Record<string, ReturnType<typeof computeStats>> = {};
    function computeStats(channelPosts: PostHistoryItem[]) {
      const nowTime = Date.now();
      const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
      const sorted = [...channelPosts].sort(
        (a, b) => new Date(b.published_date).getTime() - new Date(a.published_date).getTime(),
      );
      const sum = (m: Metric) => sorted.reduce((s, p) => s + mv(p.analytics, m), 0);
      const allTimeViews = sum('views');
      const allTimeLikes = sum('likes');
      const allTimeComments = sum('comments');
      const allTimeShares = sum('shares');

      const totalPosts = sorted.length;
      const postsLastMonthCount = sorted.filter((p) => nowTime - new Date(p.published_date).getTime() <= thirtyDaysMs).length;
      const postsPrev3MonthsCount = sorted.filter((p) => {
        const diff = nowTime - new Date(p.published_date).getTime();
        return diff > thirtyDaysMs && diff <= 4 * thirtyDaysMs;
      }).length;
      const avgPrev3Months = postsPrev3MonthsCount / 3;
      let monthlyPostsVariation = 0;
      if (avgPrev3Months === 0) { if (postsLastMonthCount > 0) monthlyPostsVariation = 100; }
      else monthlyPostsVariation = Math.round(((postsLastMonthCount - avgPrev3Months) / avgPrev3Months) * 100);

      const latest5 = sorted.slice(0, 5);
      const prev5 = sorted.slice(5, 10);
      const calcVar = (m: Metric) => {
        const cur = latest5.reduce((s, p) => s + mv(p.analytics, m), 0);
        const prev = prev5.reduce((s, p) => s + mv(p.analytics, m), 0);
        if (prev === 0) return cur > 0 ? 100 : 0;
        return Math.round(((cur - prev) / prev) * 100);
      };

      const tagsMap: Record<string, number> = {};
      sorted.forEach((p) => (p.tags || []).forEach((tg) => { if (tg) tagsMap[tg] = (tagsMap[tg] || 0) + 1; }));
      const topTags = Object.entries(tagsMap).sort((a, b) => b[1] - a[1]).slice(0, 3).map((e) => ({ text: e[0], count: e[1] }));

      const sourcesMap: Record<string, number> = {};
      sorted.forEach((p) => { if (p.source) sourcesMap[p.source] = (sourcesMap[p.source] || 0) + 1; });
      const topSources = Object.entries(sourcesMap).sort((a, b) => b[1] - a[1]).slice(0, 3).map((e) => ({ text: e[0], count: e[1] }));

      return {
        allTimeViews, allTimeLikes, allTimeComments, allTimeShares,
        totalPosts, postsLastMonthCount, monthlyPostsVariation,
        varViews: calcVar('views'), varLikes: calcVar('likes'),
        varComments: calcVar('comments'), varShares: calcVar('shares'),
        topTags, topSources,
      };
    }
    for (const c of channels) stats[c.id] = computeStats(postsByChannel[c.id] || []);
    return stats;
  }, [channels, postsByChannel]);

  // Auto-scroll sur "aujourd'hui" (droite) au mount + changement de granularité.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollToRight = () => { el.scrollLeft = el.scrollWidth; };
    scrollToRight();
    const timeoutId = setTimeout(scrollToRight, 50);
    const observer = new ResizeObserver(scrollToRight);
    observer.observe(el);
    return () => { clearTimeout(timeoutId); observer.disconnect(); };
    // posts → re-scroll vers "aujourd'hui" après le chargement async des données.
  }, [timeFrame, periodsData, posts]);

  const oldestDate = periodsData.periods[0]?.start.getTime() ?? 0;
  const newestDate = periodsData.periods[periodsData.periods.length - 1]?.end.getTime() ?? 0;
  const totalDuration = newestDate - oldestDate || 1;

  const renderVariationBadge = (val: number) => {
    const isPositive = val >= 0;
    return (
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isPositive ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`}>
        {isPositive ? '+' : ''}{val}%
      </span>
    );
  };

  const metricTabs: { id: Metric; icon: typeof BarChart2; label: string }[] = [
    { id: 'views', icon: BarChart2, label: lbl('metricViews', 'Vues') },
    { id: 'likes', icon: ThumbsUp, label: lbl('metricLikes', 'Likes') },
    { id: 'comments', icon: MessageSquare, label: lbl('metricComments', 'Commentaires') },
    { id: 'shares', icon: Share2, label: lbl('metricShares', 'Partages') },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full gap-2">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-text-secondary)' }} />
        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{lbl('loading', 'Chargement…')}</span>
      </div>
    );
  }

  if (enabledChannels.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-center px-6">
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{lbl('empty', "Pas encore de données d'historique.")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[var(--color-background)]" style={{ minHeight: 0 }}>
      {/* Header Controls */}
      <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between shrink-0">
        {/* Metric toggle group */}
        <PillToggleGroup
          items={metricTabs.map((m) => ({ id: m.id, label: m.label, icon: m.icon }))}
          value={metric}
          onChange={(id) => setMetric(id as Metric)}
          ariaLabel={lbl('metricToggleLabel', 'Métrique')}
        />

        {/* Granularity toggle group */}
        <PillToggleGroup
          items={[
            { id: 'daily', label: lbl('granDaily', 'Jour') },
            { id: 'weekly', label: lbl('granWeekly', 'Semaine') },
            { id: 'monthly', label: lbl('granMonthly', 'Mois') },
          ]}
          value={timeFrame}
          onChange={(id) => setTimeFrame(id as TimeFrame)}
          ariaLabel={lbl('granLabel', 'Granularité')}
          alwaysShowLabel
        />
      </div>

      {/* Main content area — minHeight:0 inline (min-h-0 absent du CSS figé) pour que
          le panneau latéral scrolle en interne au lieu de faire grandir la row au hover. */}
      <div ref={containerRef} className="flex-1 flex overflow-hidden relative" style={{ minHeight: 0 }}>
        {/* Left: channel labels + expanding panel */}
        <div className="shrink-0 flex z-20 relative bg-[var(--color-background)]" style={{ minHeight: 0, overflow: 'hidden' }} onMouseLeave={() => setActiveChannelId(null)}>
          {/* Fixed channel labels (80px column). Inline styles : le CSS Tailwind de Raiso
              est figé/précompilé — les classes arbitraires/inset uniques à ce fichier
              (w-[80px], inset-x-0, bottom-12, min-h-[60px]…) n'y sont pas. */}
          <div
            className="shrink-0 border-r border-[var(--color-border)] bg-[var(--color-background)] flex flex-col relative z-30"
            style={{ width: 80, boxShadow: '4px 0 10px rgba(0,0,0,0.02)' }}
          >
            <div
              className="flex flex-col justify-around"
              style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 48, paddingTop: 24, paddingBottom: 24 }}
            >
              {enabledChannels.map((channel) => {
                const cfg = getChannelDisplayConfig(channel.network);
                const isActive = activeChannelId === channel.id;
                return (
                  <div
                    key={channel.id}
                    className="flex-1 flex items-center justify-center relative"
                    style={{ minHeight: 60 }}
                    onMouseEnter={() => setActiveChannelId(channel.id)}
                  >
                    {/* Vraies icônes réseau (ChannelSvgIcon) au lieu de la 1ère lettre. */}
                    <div
                      className="rounded-full cursor-pointer transition-all duration-300"
                      style={{ transform: isActive ? 'scale(1.1)' : 'none', outline: isActive ? '2px solid var(--color-border)' : 'none', outlineOffset: 2, lineHeight: 0 }}
                    >
                      <ChannelSvgIcon
                        network={channel.network}
                        size={32}
                        fallbackClassName="text-white font-bold"
                        fallbackStyle={{ backgroundColor: cfg.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Animated details panel — height:100% + minHeight:0 inline pour contraindre la
              hauteur (sinon le panneau prend la hauteur de ses 5 accordéons et grandit la row). */}
          <motion.div
            initial={false}
            animate={{ width: activeChannelId ? 360 : 0 }}
            className="overflow-hidden flex flex-col bg-[var(--color-surface)] border-r border-[var(--color-border)] shadow-[10px_0_15px_rgba(0,0,0,0.03)] z-20 relative"
            style={{ height: '100%', minHeight: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="h-full p-5 flex flex-col overflow-y-auto" style={{ width: 360, minHeight: 0, scrollbarWidth: 'thin' }}>
              {renderedChannel && channelStats[renderedChannel.id] && (
                <div className="flex flex-col gap-2 pb-6">
                  <div className="mb-4">
                    <h4 className="font-bold text-[var(--color-text-primary)] text-xl text-center">
                      {renderedChannel.custom_name || getChannelDisplayConfig(renderedChannel.network).label || renderedChannel.network}
                    </h4>
                  </div>

                  <Accordion title={lbl('topTopics', 'Sujets abordés')}>
                    <div className="flex flex-wrap gap-2">
                      {channelStats[renderedChannel.id].topTags.length > 0 ? (
                        channelStats[renderedChannel.id].topTags.map((tag) => (
                          <span key={tag.text} className="px-3 py-1 rounded-full text-xs font-medium bg-[var(--color-background)] border border-[var(--color-border)] text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-primary)]">
                            {tag.text} <span className="opacity-50 font-normal ml-1">({tag.count})</span>
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-[var(--color-text-secondary)] italic">{lbl('noData', 'Aucune donnée')}</span>
                      )}
                    </div>
                  </Accordion>

                  <Accordion title={lbl('topSources', 'Sources principales')}>
                    <ul className="space-y-2">
                      {channelStats[renderedChannel.id].topSources.length > 0 ? (
                        channelStats[renderedChannel.id].topSources.map((source) => (
                          <li key={source.text} className="flex justify-between items-center bg-[var(--color-background)] px-3 py-2 rounded-full border border-[var(--color-border)]">
                            <span className="truncate font-medium text-sm text-[var(--color-text-primary)]">{source.text}</span>
                            <span className="text-xs font-bold text-[var(--color-text-secondary)] bg-[var(--color-surface)] px-2 py-1 rounded-full">{source.count}</span>
                          </li>
                        ))
                      ) : (
                        <span className="text-xs text-[var(--color-text-secondary)] italic">{lbl('noData', 'Aucune donnée')}</span>
                      )}
                    </ul>
                  </Accordion>

                  <Accordion title={lbl('postsActivity', 'Activité de publication')}>
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] text-[var(--color-text-secondary)] mb-1 uppercase tracking-wider">{lbl('totalPosts', 'Total')}</span>
                        <span className="text-xl font-black text-[var(--color-text-primary)]">{channelStats[renderedChannel.id].totalPosts}</span>
                      </div>
                      <div className="w-px h-10 bg-[var(--color-border)]" />
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] text-[var(--color-text-secondary)] mb-1 uppercase tracking-wider">{lbl('postsLastMonth', 'Dernier mois')}</span>
                        <span className="text-xl font-black text-[var(--color-text-primary)]">{channelStats[renderedChannel.id].postsLastMonthCount}</span>
                      </div>
                      <div className="w-px h-10 bg-[var(--color-border)]" />
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] text-[var(--color-text-secondary)] mb-1 uppercase tracking-wider">{lbl('postsVariation', 'Variation')}</span>
                        {renderVariationBadge(channelStats[renderedChannel.id].monthlyPostsVariation)}
                      </div>
                    </div>
                  </Accordion>

                  <Accordion title={lbl('globalStats', 'Statistiques globales')}>
                    <div className="grid grid-cols-2 gap-4">
                      {([
                        { icon: BarChart2, label: lbl('metricViews', 'Vues'), value: channelStats[renderedChannel.id].allTimeViews, varv: channelStats[renderedChannel.id].varViews },
                        { icon: ThumbsUp, label: lbl('metricLikes', 'Likes'), value: channelStats[renderedChannel.id].allTimeLikes, varv: channelStats[renderedChannel.id].varLikes },
                        { icon: MessageSquare, label: lbl('metricComments', 'Commentaires'), value: channelStats[renderedChannel.id].allTimeComments, varv: channelStats[renderedChannel.id].varComments },
                        { icon: Share2, label: lbl('metricShares', 'Partages'), value: channelStats[renderedChannel.id].allTimeShares, varv: channelStats[renderedChannel.id].varShares },
                      ]).map((s) => (
                        <div key={s.label} className="flex flex-col">
                          <span className="text-xs text-[var(--color-text-secondary)] flex items-center gap-1 mb-1">
                            <s.icon className="w-3 h-3" /> {s.label}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-base font-bold text-[var(--color-text-primary)]">{s.value.toLocaleString(locale)}</span>
                            {renderVariationBadge(s.varv)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Accordion>

                  <Accordion title={lbl('positioningTitle', 'Positionnement')}>
                    <div className="flex flex-col gap-3">
                      {(() => {
                        const hasPositioning = !!(renderedChannel.positioning || posDraft[renderedChannel.id]);
                        const draft = posDraft[renderedChannel.id] ?? renderedChannel.positioning ?? '';
                        return (
                          <>
                            <textarea
                              value={draft}
                              onChange={(e) => setPosDraft((prev) => ({ ...prev, [renderedChannel.id]: e.target.value }))}
                              placeholder={lbl('noPositioning', 'Aucun positionnement spécifique défini pour ce canal.')}
                              rows={5}
                              className="w-full text-sm rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-text-primary)] p-3"
                              style={{ minHeight: 96, resize: 'vertical' }}
                            />
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleSavePositioning(renderedChannel)}
                                disabled={posBusy !== 'idle'}
                                className="flex-1 flex items-center justify-center gap-2 py-2 bg-[var(--color-primary)] text-[var(--color-button-text)] rounded-full text-xs font-medium shadow-sm"
                                style={{ opacity: posBusy !== 'idle' ? 0.6 : 1, cursor: posBusy !== 'idle' ? 'not-allowed' : 'pointer' }}
                              >
                                {posBusy === 'saving' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                {lbl('savePositioning', 'Enregistrer')}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleGeneratePositioning(renderedChannel)}
                                disabled={posBusy !== 'idle'}
                                className="flex-1 flex items-center justify-center gap-2 py-2 bg-[var(--color-background)] border border-[var(--color-border)] text-[var(--color-text-primary)] rounded-full text-xs font-medium shadow-sm"
                                style={{ opacity: posBusy !== 'idle' ? 0.6 : 1, cursor: posBusy !== 'idle' ? 'not-allowed' : 'pointer' }}
                              >
                                {posBusy === 'generating'
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : hasPositioning ? <TrendingUp className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                                {hasPositioning ? lbl('improvePositioning', 'Améliorer') : lbl('generatePositioning', 'Générer')}
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </Accordion>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Scrollable timeline — min-w-0 indispensable : sans lui, ce flex-item grandit à la
            largeur du contenu (~48000px) au lieu de scroller, et le contexte de positionnement
            des points collapse (offsetParent width 0 → points collés à gauche). */}
        <div
          ref={scrollRef}
          data-testid="history-timeline-scroll"
          className="flex-1 min-w-0"
          style={{ overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'thin' }}
          onScroll={() => setHoveredPoint(null)}
        >
          <div className="h-full flex flex-col" style={{ width: `${periodsData.periods.length * periodsData.periodWidth}px`, minWidth: '100%' }}>
            <div className="flex-1 relative">
              {/* Background period columns */}
              <div className="absolute inset-0 flex">
                {periodsData.periods.map((period, i) => (
                  <div key={i} className="flex-1 border-r border-[var(--color-border)] opacity-30 relative">
                    <div className="absolute text-xs text-[var(--color-text-secondary)] whitespace-nowrap" style={{ bottom: 16, right: 8 }}>{period.label}</div>
                  </div>
                ))}
              </div>

              {/* Foreground channel streams */}
              <div
                className="flex flex-col justify-around pointer-events-none"
                style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 48, paddingTop: 24, paddingBottom: 24 }}
              >
                {enabledChannels.map((channel) => {
                  const cfg = getChannelDisplayConfig(channel.network);
                  const channelPosts = postsByChannel[channel.id] || [];
                  const maxMetric = Math.max(...channelPosts.map((p) => mv(p.analytics, metric)), 1);

                  const pts = channelPosts.map((post) => {
                    const postTime = new Date(post.published_date).getTime();
                    const x = ((postTime - oldestDate) / totalDuration) * 100;
                    const y = 100 - (mv(post.analytics, metric) / maxMetric) * 100;
                    return { x, y, post, postTime };
                  });

                  let d = '';
                  if (pts.length === 1) d = `M ${pts[0].x} ${pts[0].y} L ${pts[0].x} ${pts[0].y}`;
                  else if (pts.length > 1) {
                    d = `M ${pts[0].x} ${pts[0].y}`;
                    for (let i = 0; i < pts.length - 1; i++) {
                      const p1 = pts[i]; const p2 = pts[i + 1];
                      const mx = p1.x + (p2.x - p1.x) / 2;
                      d += ` C ${mx} ${p1.y}, ${mx} ${p2.y}, ${p2.x} ${p2.y}`;
                    }
                  }

                  return (
                    <div key={channel.id} className="flex-1 w-full relative group" style={{ minHeight: 60, paddingLeft: 16, paddingRight: 16 }}>
                      <div style={{ position: 'absolute', left: 16, right: 16, top: 16, bottom: 16 }}>
                        <svg className="w-full h-full pointer-events-none" style={{ position: 'absolute', inset: 0, overflow: 'visible' }} preserveAspectRatio="none" viewBox="0 0 100 100">
                          <path d={d} fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke" style={{ stroke: cfg.color }} className="opacity-40 group-hover:opacity-80 transition-opacity duration-300" />
                        </svg>
                        {pts.map((pt) => {
                          if (pt.postTime < oldestDate || pt.postTime > newestDate) return null;
                          return (
                            <div
                              key={pt.post.id}
                              className="w-3 h-3 rounded-full border-2 border-[var(--color-background)] cursor-pointer transition-all z-20 shadow-sm"
                              style={{ position: 'absolute', left: `${pt.x}%`, top: `${pt.y}%`, marginLeft: -6, marginTop: -6, backgroundColor: cfg.color, pointerEvents: 'auto', zIndex: 20 }}
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setHoveredPoint({ x: rect.left, y: rect.top, post: pt.post });
                              }}
                              onMouseLeave={() => setHoveredPoint(null)}
                              onClick={() => onPostClick?.(pt.post.id)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hovered point tooltip */}
      <AnimatePresence>
        {hoveredPoint && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed z-50 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-xl p-4 w-64 pointer-events-none"
            style={{ left: hoveredPoint.x - 128 + 8, top: hoveredPoint.y - 140 }}
          >
            <div className="text-xs text-[var(--color-text-secondary)] mb-2 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {hoveredPoint.post.published_date
                ? new Date(hoveredPoint.post.published_date).toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                : '—'}
            </div>
            {hoveredPoint.post.analytics_status === 'unavailable' ? (
              <div className="text-xs italic mb-3 py-2 text-center" style={{ color: 'var(--color-text-secondary)' }}>
                {lbl('analyticsUnavailable', 'Analytics indisponibles (add-on Zernio requis)')}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 mb-3">
                {([
                  { icon: BarChart2, label: lbl('metricViews', 'Vues'), value: hoveredPoint.post.analytics.views },
                  { icon: ThumbsUp, label: lbl('metricLikes', 'Likes'), value: hoveredPoint.post.analytics.likes },
                  { icon: MessageSquare, label: lbl('metricComments', 'Commentaires'), value: hoveredPoint.post.analytics.comments_count },
                  { icon: Share2, label: lbl('metricShares', 'Partages'), value: hoveredPoint.post.analytics.shares },
                ]).map((s) => (
                  <div key={s.label} className="flex flex-col">
                    <span className="text-[10px] uppercase text-[var(--color-text-secondary)] font-medium mb-1 flex items-center gap-1">
                      <s.icon className="w-3 h-3" /> {s.label}
                    </span>
                    <span className="text-sm font-bold text-[var(--color-text-primary)]">{Number(s.value ?? 0).toLocaleString(locale)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[10px] text-center text-[var(--color-primary)] font-medium flex items-center justify-center gap-1 pt-2 border-t border-[var(--color-border)]">
              {lbl('openPost', 'Ouvrir le post')} <ExternalLink className="w-3 h-3" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
